// ==========================================
// FIELD TYPE SYSTEM
// ==========================================
// These types define the mathematical domains for variables.
// They are used to create type-safe shapes for representations.

// -----------------------------
// 1. BASE FIELD INTERFACE
// -----------------------------

interface BaseField {
  /** Display label for the field. If not provided, the field key is used. */
  label?: string;
  /** How to render the label: 'latex' for KaTeX math notation (e.g., '$u_1$'), 'text' for plain text. Defaults to 'text'. */
  label_type?: 'latex' | 'text';
  description?: string;
  example?: string | number | boolean | unknown;
  /** Marks this field as optional for procedure inputs (metadata/display fields). Required fields (without optional: true) get darker styling in the UI. */
  optional?: boolean;
}

// -----------------------------
// 2. NUMBER DOMAIN TYPES
// -----------------------------

/**
 * Mathematical domains for numeric fields.
 *
 * - `real` - ℝ: All real numbers (e.g., -2.5, 0, π, 100.7)
 * - `integer` - ℤ: Whole numbers (e.g., -3, -1, 0, 1, 5)
 * - `natural` - ℕ: Counting numbers starting from 1 (e.g., 1, 2, 3, 4, 5)
 * - `positive` - ℝ⁺: Numbers greater than 0 (e.g., 0.001, 1, π)
 * - `negative` - ℝ⁻: Numbers less than 0 (e.g., -0.001, -1, -π)
 */
export type NumberDomain = 'real' | 'integer' | 'natural' | 'positive' | 'negative';

// -----------------------------
// 3. NUMBER FIELD
// -----------------------------

/**
 * A numeric field with optional mathematical domain constraints.
 *
 * @example
 * ```ts
 * // A real number (default)
 * { type: 'number' }
 *
 * // An integer
 * { type: 'number', domain: 'integer' }
 *
 * // A natural number (1, 2, 3, ...)
 * { type: 'number', domain: 'natural', min: 1 }
 * ```
 */
export interface NumberField extends BaseField {
  type: 'number';
  /**
   * The mathematical domain/set this number belongs to.
   *
   * - `real` - ℝ: All real numbers (e.g., -2.5, 0, π, 100.7)
   * - `integer` - ℤ: Whole numbers (e.g., -3, -1, 0, 1, 5)
   * - `natural` - ℕ: Counting numbers starting from 1 (e.g., 1, 2, 3, 4, 5)
   * - `positive` - ℝ⁺: Numbers greater than 0 (e.g., 0.001, 1, π)
   * - `negative` - ℝ⁻: Numbers less than 0 (e.g., -0.001, -1, -π)
   */
  domain?: NumberDomain;

  /** Minimum value (inclusive by default) */
  min?: number;
  /** Maximum value (inclusive by default) */
  max?: number;
  /** If true, the minimum is exclusive (value > min, not value >= min) */
  min_exclusive?: boolean;
  /** If true, the maximum is exclusive (value < max, not value <= max) */
  max_exclusive?: boolean;
  /** Specific values to exclude (e.g., exclude 0 for division) */
  exclude?: number[];
}

// -----------------------------
// 4. ENUM FIELD
// -----------------------------

/**
 * A field with a finite set of valid string values.
 *
 * @example
 * ```ts
 * { type: 'enum', options: ['INCREASING', 'DECREASING', 'CONSTANT'] }
 * ```
 */
export interface EnumField<T extends string = string> extends BaseField {
  type: 'enum';
  options: readonly T[];
}

// -----------------------------
// 5. BOOLEAN FIELD
// -----------------------------

/**
 * A true/false field.
 */
export interface BooleanField extends BaseField {
  type: 'boolean';
}

// -----------------------------
// 6. STRING FIELD
// -----------------------------

/**
 * A text field with optional format hint.
 *
 * @example
 * ```ts
 * { type: 'string', format: 'latex' }  // For mathematical notation
 * { type: 'string', format: 'plain' }  // Plain text
 * ```
 */
export interface StringField extends BaseField {
  type: 'string';
  /** Display/parsing format hint */
  format?: 'latex' | 'plain' | 'markdown';
}

// -----------------------------
// 7. ARRAY FIELD
// -----------------------------

/**
 * A list/sequence of items of a specific type.
 *
 * @example
 * ```ts
 * { type: 'array', items: { type: 'number' }, minLength: 1 }
 * ```
 */
export interface ArrayField<T extends FieldDefinition = FieldDefinition> extends BaseField {
  type: 'array';
  items: T;
  minLength?: number;
  maxLength?: number;
}

// -----------------------------
// 8. CONSTANT FIELD
// -----------------------------
// Mathematical constants (π, e, φ, etc.)
// These are first-class entities that can have documentation attached.

export type MathConstant = 'pi' | 'e' | 'phi' | 'sqrt2' | 'sqrt3' | 'ln2' | 'ln10' | 'infinity';

export interface ConstantField extends BaseField {
  type: 'constant';
  value: MathConstant;
}

// Runtime values for constants
export const MATH_CONSTANT_VALUES: Record<MathConstant, number> = {
  pi: Math.PI,
  e: Math.E,
  phi: (1 + Math.sqrt(5)) / 2,
  sqrt2: Math.SQRT2,
  sqrt3: Math.sqrt(3),
  ln2: Math.LN2,
  ln10: Math.LN10,
  infinity: Infinity,
};

// Display symbols for constants
export const MATH_CONSTANT_SYMBOLS: Record<MathConstant, string> = {
  pi: 'π',
  e: 'e',
  phi: 'φ',
  sqrt2: '√2',
  sqrt3: '√3',
  ln2: 'ln(2)',
  ln10: 'ln(10)',
  infinity: '∞',
};

// -----------------------------
// UNION TYPE
// -----------------------------

export type FieldDefinition = NumberField | EnumField | BooleanField | StringField | ArrayField | ConstantField;

// -----------------------------
// TYPE INFERENCE HELPERS
// -----------------------------
// These help TypeScript infer the runtime type from a field definition.

export type InferFieldType<F extends FieldDefinition> = F extends NumberField
  ? number
  : F extends EnumField<infer T>
    ? T
    : F extends BooleanField
      ? boolean
      : F extends StringField
        ? string
        : F extends ArrayField<infer T>
          ? Array<InferFieldType<T>>
          : F extends ConstantField
            ? number
            : never;

// Helper to extract keys of optional fields
type OptionalFieldKeys<M extends Record<string, FieldDefinition>> = {
  [K in keyof M]: M[K] extends { optional: true } ? K : never;
}[keyof M];

// Helper to extract keys of required fields
type RequiredFieldKeys<M extends Record<string, FieldDefinition>> = {
  [K in keyof M]: M[K] extends { optional: true } ? never : K;
}[keyof M];

// Infer the model (runtime type) from a record of fields
// Fields marked with `optional: true` become optional in the inferred type
export type InferModel<M extends Record<string, FieldDefinition>> = {
  [K in RequiredFieldKeys<M>]: InferFieldType<M[K]>;
} & {
  [K in OptionalFieldKeys<M>]?: InferFieldType<M[K]>;
};

// Infer ONLY required fields from a model
// Used for procedure inputs - procedures can only rely on required fields
export type InferRequiredModel<M extends Record<string, FieldDefinition>> = {
  [K in RequiredFieldKeys<M>]: InferFieldType<M[K]>;
};

// -----------------------------
// FIELD CONSTRUCTORS
// -----------------------------
// Helper functions to create fields with proper typing.

export const field = {
  /**
   * A real number field (ℝ). Can be any decimal value.
   * @example field.number({ description: 'Any real number' })
   */
  number: <T extends Omit<NumberField, 'type'>>(opts?: T): NumberField & T =>
    ({
      type: 'number',
      ...opts,
    }) as NumberField & T,

  /**
   * An integer field (ℤ). Whole numbers only: ..., -2, -1, 0, 1, 2, ...
   * @example field.integer({ description: 'A whole number' })
   */
  integer: <T extends Omit<NumberField, 'type' | 'domain'>>(opts?: T): NumberField & T =>
    ({
      type: 'number',
      domain: 'integer',
      ...opts,
    }) as NumberField & T,

  /**
   * A natural number field (ℕ). Counting numbers starting from 1: 1, 2, 3, 4, ...
   * Used for indices, positions, counts, etc.
   * @example field.natural({ description: 'Term position' })
   */
  natural: <T extends Omit<NumberField, 'type' | 'domain' | 'min'>>(opts?: T): NumberField & T =>
    ({
      type: 'number',
      domain: 'natural',
      min: 1,
      ...opts,
    }) as NumberField & T,

  /**
   * A positive number field (ℝ⁺). Any number greater than 0.
   * @example field.positive({ description: 'Must be positive' })
   */
  positive: <T extends Omit<NumberField, 'type' | 'domain' | 'min' | 'min_exclusive'>>(opts?: T): NumberField & T =>
    ({
      type: 'number',
      domain: 'positive',
      min: 0,
      min_exclusive: true,
      ...opts,
    }) as NumberField & T,

  /**
   * An enum field with a finite set of valid string values.
   * @example field.enum(['INCREASING', 'DECREASING', 'CONSTANT'] as const)
   */
  enum: <T extends string, O extends Omit<EnumField<T>, 'type' | 'options'>>(
    options: readonly T[],
    opts?: O,
  ): EnumField<T> & O =>
    ({
      type: 'enum',
      options,
      ...opts,
    }) as EnumField<T> & O,

  /**
   * A boolean field (true/false).
   * @example field.boolean({ description: 'Is convergent?' })
   */
  boolean: <T extends Omit<BooleanField, 'type'>>(opts?: T): BooleanField & T =>
    ({
      type: 'boolean',
      ...opts,
    }) as BooleanField & T,

  /**
   * A plain text string field.
   * @example field.string({ description: 'Label text' })
   */
  string: <T extends Omit<StringField, 'type'>>(opts?: T): StringField & T =>
    ({
      type: 'string',
      ...opts,
    }) as StringField & T,

  /**
   * A LaTeX-formatted string field for mathematical notation.
   * @example field.latex({ description: 'Formula in LaTeX' })
   */
  latex: <T extends Omit<StringField, 'type' | 'format'>>(opts?: T): StringField & T =>
    ({
      type: 'string',
      format: 'latex',
      ...opts,
    }) as StringField & T,

  /**
   * An array/list field containing items of a specific type.
   * @example field.array(field.number(), { minLength: 1 })
   */
  array: <T extends FieldDefinition, O extends Omit<ArrayField<T>, 'type' | 'items'>>(
    items: T,
    opts?: O,
  ): ArrayField<T> & O =>
    ({
      type: 'array',
      items,
      ...opts,
    }) as ArrayField<T> & O,

  /**
   * An array of LaTeX-formatted strings for lists of mathematical expressions.
   * @example field.latexArray({ description: 'List of factors', example: ['(x + 1)', '(x - 2)'] })
   */
  latexArray: <T extends Omit<ArrayField<StringField>, 'type' | 'items'>>(opts?: T): ArrayField<StringField> & T =>
    ({
      type: 'array',
      items: { type: 'string', format: 'latex' },
      ...opts,
    }) as ArrayField<StringField> & T,

  /**
   * A mathematical constant field (π, e, φ, etc.).
   * @example field.constant('pi')
   */
  constant: <T extends Omit<ConstantField, 'type' | 'value'>>(value: MathConstant, opts?: T): ConstantField & T =>
    ({
      type: 'constant',
      value,
      ...opts,
    }) as ConstantField & T,

  /**
   * Pi (π) - The ratio of a circle's circumference to its diameter.
   * Approximately 3.14159...
   */
  pi: <T extends Omit<ConstantField, 'type' | 'value'>>(opts?: T): ConstantField & T =>
    ({
      type: 'constant',
      value: 'pi',
      description: "The ratio of a circle's circumference to its diameter",
      ...opts,
    }) as ConstantField & T,

  /**
   * Euler's number (e) - The base of natural logarithms.
   * Approximately 2.71828...
   */
  e: <T extends Omit<ConstantField, 'type' | 'value'>>(opts?: T): ConstantField & T =>
    ({
      type: 'constant',
      value: 'e',
      description: "Euler's number, the base of natural logarithms",
      ...opts,
    }) as ConstantField & T,

  /**
   * The golden ratio (φ) - (1 + √5) / 2.
   * Approximately 1.61803...
   */
  phi: <T extends Omit<ConstantField, 'type' | 'value'>>(opts?: T): ConstantField & T =>
    ({
      type: 'constant',
      value: 'phi',
      description: 'The golden ratio (1 + √5) / 2',
      ...opts,
    }) as ConstantField & T,
};
